import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Form, Modal } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import OrderProductForm from "../components/Order/OrderProductForm";
import OrderCustomerForm from "../components/Order/OrderCustomerForm";
import DeliveryFrom from "../components/Order/DeliveryFrom";
import OrderFormRail from "../components/Order/OrderFormRail";
import Loading from "../components/Util/Loading";
import { extractOrderNumber } from "../components/Util/OrderNumberFormatter";
import useAxiosClient from "../axios-client";
import { useStateContext } from "../contexts/ContextProvider";
import { OrderTypeEnum } from "../utils/enums/OrderTypeEnum";
import { useMarketplaces } from "../hooks/useMarketplaces";
import { useProductTypes } from "../hooks/useProductTypes";
import { useFabricTypes } from "../hooks/useFabricTypes";
import { useProductFormations } from "../hooks/useProductFormations";
import { useDivisions } from "../hooks/useDivisions";
import { useDistricts } from "../hooks/useDistricts";
import { useUpazilas } from "../hooks/useUpazilas";
import { useFabrics } from "../hooks/useFabrics";
import { useFabricBalances } from "../hooks/useFabricBalances";
import {
    errorLabel,
    isFilled,
    Mono,
    orderFormTheme,
    PRODUCT_FIELD_LABELS,
    PRODUCT_FIELDS,
    readiness,
    sectionOfField,
} from "../components/Order/orderFormShared";
import "../components/Order/orderForm.css";

const DRAFT_KEY = "ET_ORDER_DRAFT_V1";
const EMPTY_VALUES = {};
const SECTION_ANCHORS = {
    products: "et-of-section-products",
    customer: "et-of-section-customer",
    delivery: "et-of-section-delivery",
};

/** Field values that survive a tab close. Uploads deliberately do not. */
const serialiseValues = (values) => {
    const { images, deliveryDate, ...rest } = values || {};
    return JSON.stringify({
        ...rest,
        deliveryDate: deliveryDate ? dayjs(deliveryDate).format("YYYY-MM-DD") : undefined,
    });
};

const parseDraft = (raw) => {
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        return {
            ...parsed,
            deliveryDate: parsed.deliveryDate ? dayjs(parsed.deliveryDate, "YYYY-MM-DD") : undefined,
        };
    } catch {
        return null;
    }
};

/**
 * Whether a form state is worth persisting or announcing. A pristine form still
 * has one empty product line and antd's own nulls, and neither a draft write nor
 * a "restored your draft" toast means anything for that.
 */
const hasContent = (values) => {
    const { images, products, ...rest } = values || {};
    if (Object.values(rest).some(isFilled)) return true;
    // Description and pattern are not in PRODUCT_FIELDS — they are optional —
    // but they are still typing worth saving a draft for.
    return (products || []).some((p) =>
        PRODUCT_FIELDS.some((f) => isFilled(p?.[f]))
        || isFilled(p?.productDescription)
        || isFilled(p?.fabricPattern));
};

/** Lines that cannot be submitted, with what each one is still missing. */
const incompleteLines = (products) =>
    (products || [])
        .map((p, i) => ({ index: i, missing: PRODUCT_FIELDS.filter((f) => !isFilled(p?.[f])) }))
        .filter((r) => r.missing.length > 0);

/**
 * Create / Edit Order.
 *
 * One page, two modes. Edit differs from create in exactly three places: the
 * channel is locked, a baseline snapshot backs the "fields changed" count, and
 * it PUTs instead of POSTing. Everything else — layout, validation, the
 * readiness rail — is shared, which is why these are no longer two files.
 *
 * The page is a single scroll rather than a wizard: order entry is non-linear
 * (a phone call gives you the customer before the products) and operators
 * backtrack constantly. Progress lives in the rail instead of in step headers.
 */
const OrderFormPage = ({ mode = "create" }) => {
    const isEdit = mode === "edit";
    const axiosClient = useAxiosClient();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { id } = useParams();
    const { user, roles, permissions, darkMode } = useStateContext();
    const [orderForm] = Form.useForm();

    const orderId = isEdit ? (id ? extractOrderNumber(id) : state?.order?.id) : null;

    const [files, setFiles] = useState([]);
    const [removedFiles, setRemovedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [fieldErrors, setFieldErrors] = useState([]);

    const [order, setOrder] = useState({});
    const [orderLoading, setOrderLoading] = useState(isEdit);
    const [baseline, setBaseline] = useState(null);
    const [draftSavedAt, setDraftSavedAt] = useState(null);
    const [startedAt] = useState(() => dayjs());

    const statusVal = typeof order?.status === 'object' ? order?.status?.value : order?.status;
    const isApproved = statusVal != null && Number(statusVal) !== 1;
    const hasAdminPermission = (roles || []).some((r) => ['SUDO', 'Admin'].includes(r))
        || (permissions || []).includes('EDIT_APPROVED_ORDER_PRICE');
    const isPriceLocked = Boolean(isEdit && isApproved && !hasAdminPermission);

    // Create mode owns the channel; edit mode reads it off the loaded order.
    const [orderType, setOrderType] = useState(state?.orderType ?? OrderTypeEnum.MARKETPLACE);
    const effectiveType = isEdit ? order.orderType : orderType;
    const channel = effectiveType === OrderTypeEnum.MARKETPLACE ? "online" : "offline";

    const { marketplaces } = useMarketplaces();
    // Edit mode: the order's marketplace may not be in the current user's
    // assigned list, which would make the Select show its raw id instead of
    // the name. Augment the list so the label always resolves.
    const [extraMarketplaces, setExtraMarketplaces] = useState([]);
    const allMarketplaces = useMemo(() => {
        const existing = new Map(marketplaces.map((m) => [m.id, m]));
        extraMarketplaces.forEach((m) => existing.set(m.id, m));
        return Array.from(existing.values());
    }, [marketplaces, extraMarketplaces]);
    const { productTypes } = useProductTypes();
    const { fabricTypes } = useFabricTypes();
    const { productFormations, formationsByType } = useProductFormations({ includeLegacy: isEdit });
    const { fabrics, loadMore, hasMore, fabricsLoading, fetchFabrics, setFabrics } = useFabrics();
    // What the fabric store holds, so a line can say so as the cloth is picked.
    const { onHand } = useFabricBalances();

    const [selectedDivision, setSelectedDivision] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const { divisions, divisionLoading } = useDivisions();
    const { districts, districtLoading } = useDistricts(selectedDivision);
    const { upazilas, upazilaLoading } = useUpazilas(selectedDistrict);

    // `preserve: true` watches the whole store, not just currently-mounted
    // fields — collapsed product lines unmount their inputs but keep their values.
    // Memoised so the identity only changes when a value does; the debounced
    // autosave below is keyed on it.
    const watched = Form.useWatch([], { form: orderForm, preserve: true });
    const values = useMemo(() => watched || EMPTY_VALUES, [watched]);

    /* ---- Totals -------------------------------------------------------- */
    const { orderAmount, grandTotal } = useMemo(() => {
        const amount = (values.products || []).reduce(
            (sum, p) => sum + (Number(p?.quantity) || 0) * (Number(p?.price) || 0),
            0,
        );
        return { orderAmount: amount, grandTotal: amount + (Number(values.deliveryCharge) || 0) };
    }, [values.products, values.deliveryCharge]);

    /* ---- Readiness + per-section error counts --------------------------- */
    const rail = useMemo(() => {
        const base = readiness(values, channel);

        // Customer and delivery counts come from antd; product lines are counted
        // whole, because a collapsed line's inputs are unmounted and the row
        // itself is what turns red.
        const antdCounts = fieldErrors.reduce((acc, f) => {
            const key = sectionOfField(f.name);
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
        const badLines = submitAttempted ? incompleteLines(values.products).length : 0;

        const lines = (values.products || []).length || 1;
        const captions = {
            products: orderAmount > 0
                ? `${lines} line${lines === 1 ? "" : "s"}`
                : `${lines} line${lines === 1 ? "" : "s"}, nothing entered`,
            customer: values.name
                ? `${channel === "online" ? "Online" : "Offline"} · ${values.name}`
                : `${channel === "online" ? "Online" : "Offline"} · no customer yet`,
            delivery: values.deliveryDate
                ? `Due ${dayjs(values.deliveryDate).format("D MMM")}`
                : "No date set",
        };

        return {
            ...base,
            sections: base.sections.map((s) => ({
                ...s,
                errors: s.key === "products" ? badLines : antdCounts[s.key] || 0,
                caption: captions[s.key],
                amount: s.key === "products"
                    ? orderAmount
                    : s.key === "delivery" ? Number(values.deliveryCharge) || 0 : null,
            })),
        };
    }, [values, channel, fieldErrors, submitAttempted, orderAmount]);

    // Once submitted, keep the rail's counts honest as fields get fixed.
    useEffect(() => {
        if (!submitAttempted) return;
        setFieldErrors(orderForm.getFieldsError().filter((e) => e.errors.length > 0));
    }, [values, submitAttempted, orderForm]);

    /* ---- Address cascades ---------------------------------------------- */
    const onDivisionSelect = (data) => {
        orderForm.setFieldValue('district', null);
        orderForm.setFieldValue('upazila', null);
        setSelectedDivision(data);
        setSelectedDistrict(null);
    };
    const onDistrictSelect = (data) => {
        orderForm.setFieldValue('upazila', null);
        setSelectedDistrict(data);
    };

    /* ---- Edit mode: load the order ------------------------------------- */
    const mapOrderToValues = useCallback((ord) => {
        const addr = ord.customer?.address;
        const mapped = {
            customerId: ord.customer?.id,
            clientOrderNumber: ord.clientOrderNumber ?? null,
            name: ord.customer?.name,
            division: addr ? +addr.division?.value : null,
            district: addr ? +addr.district?.value : null,
            upazila: addr?.upazila?.value ? +addr.upazila.value : null,
            address: addr?.address,
            facebookId: ord.customer?.facebook,
            phone: ord.customer?.phone ?? addr?.phone,
            altPhone: ord.customer?.altPhone,
            deliveryCharge: ord.payment.deliveryCharge,
            deliveryDate: dayjs(ord.deliveryDate, "YYYY-MM-DD"),
            deliveryChannel: +ord.deliveryChannel.value,
            products: ord.products.map((product) => ({
                productDescription: product.description,
                quantity: product.unit,
                price: product.price,
                productType: product.productType.value,
                fabrics: product.fabrics.value,
                fabricType: product.fabricType.value,
                // Null for a line placed before the pattern book existed, which
                // the picker reads as "nothing chosen" rather than an error.
                fabricPattern: product.fabricPattern?.value ?? null,
                productFormation: product.productFormation.value,
            })),
        };
        if (ord.orderType === OrderTypeEnum.MARKETPLACE) {
            mapped.marketplace = ord.marketplace?.id ?? ord.orderable?.id;
        }
        return mapped;
    }, []);

    useEffect(() => {
        if (!isEdit) return undefined;
        let cancelled = false;

        async function fetchOrder() {
            if (!orderId) return;
            try {
                setOrderLoading(true);
                const result = await axiosClient.get(`/orders/getOrder/${orderId}`);
                if (cancelled) return;
                const o = result.data.data;
                setOrder({ ...o });

                const addr = o.customer?.address;
                if (addr) {
                    setSelectedDivision(+addr.division?.value);
                    setSelectedDistrict(+addr.district?.value);
                }

                // Pre-fill the fabrics list so existing lines can show their names.
                const existingFabrics = o.products.map((p) => ({
                    id: p.fabrics.value,
                    name: p.fabrics.name,
                }));
                setFabrics((prev) => {
                    const combined = [...prev];
                    existingFabrics.forEach((ef) => {
                        if (!combined.find((f) => f.id === ef.id)) combined.push(ef);
                    });
                    return combined;
                });

                setFiles(o.images.map((image) => ({
                    ...image,
                    url: `${process.env.REACT_APP_API_BASE_URL}/files/upload/${image.id}`,
                })));

                // Ensure the order's marketplace is in the dropdown list even
                // when not assigned to this user, so the Select shows the name.
                if (o.marketplace?.id && o.marketplace?.name) {
                    setExtraMarketplaces((prev) => {
                        if (prev.find((m) => m.id === o.marketplace.id)) return prev;
                        return [...prev, { id: o.marketplace.id, name: o.marketplace.name }];
                    });
                }

                const newValues = mapOrderToValues(o);
                orderForm.setFieldsValue(newValues);
                // Baseline for the "fields changed" count in the rail.
                setBaseline(serialiseValues(newValues));
                setOrderLoading(false);
            } catch (error) {
                if (cancelled) return;
                const message = (error.response && error.response.data && error.response.data.message)
                    || error.message || error.toString();
                toast.error(message);
                setOrderLoading(false);
            }
        }

        fetchOrder();
        return () => { cancelled = true; };
    }, [isEdit, axiosClient, orderId, orderForm, setFabrics, mapOrderToValues]);

    /* ---- Create mode: draft restore + autosave -------------------------- */
    const draftRestored = useRef(false);
    useEffect(() => {
        if (isEdit || draftRestored.current) return;
        draftRestored.current = true;
        const parsed = parseDraft(localStorage.getItem(DRAFT_KEY));
        if (!parsed || !hasContent(parsed)) return;
        orderForm.setFieldsValue(parsed);
        if (parsed.division) setSelectedDivision(parsed.division);
        if (parsed.district) setSelectedDistrict(parsed.district);
        toast.info("Restored your unsaved draft.");
    }, [isEdit, orderForm]);

    const saveDraft = useCallback((notify) => {
        if (isEdit) return;
        const current = orderForm.getFieldsValue(true);
        if (!hasContent(current)) {
            if (notify) toast.info("Nothing to save yet.");
            return;
        }
        localStorage.setItem(DRAFT_KEY, serialiseValues(current));
        setDraftSavedAt(dayjs().format("HH:mm"));
        if (notify) toast.success("Draft saved.");
    }, [isEdit, orderForm]);

    // Debounced autosave — the rail promises the draft restores if the tab closes.
    useEffect(() => {
        if (isEdit) return undefined;
        const t = setTimeout(() => saveDraft(false), 800);
        return () => clearTimeout(t);
    }, [values, isEdit, saveDraft]);

    const changedCount = useMemo(() => {
        if (!isEdit || !baseline) return 0;
        const before = JSON.parse(baseline);
        const after = JSON.parse(serialiseValues(values));
        return Object.keys({ ...before, ...after }).filter(
            (k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]),
        ).length;
    }, [isEdit, baseline, values]);

    /* ---- Submit --------------------------------------------------------- */
    // Throws on failure: an order saved without its reference images looks
    // successful and is only noticed later, so the submit stops instead.
    const uploadFile = async (formData) => {
        const response = await axiosClient.post('/files/uploadProductImage', formData);
        return response.data;
    };

    const removeImages = async (imagePath) => {
        try {
            await axiosClient.post('/files/delete', { imagePath });
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message)
                || error.message || error.toString();
            toast.error(message);
        }
    };

    const isFormDataEmpty = (formData) => formData.entries().next().done;

    const onFinish = async () => {
        // Collapsed lines unmount their inputs, so antd's own pass cannot see
        // them. The whole-store check below is what the red rows report.
        const data = orderForm.getFieldsValue(true);
        const bad = incompleteLines(data.products);
        if (bad.length || !(data.products || []).length) {
            setSubmitAttempted(true);
            const first = bad[0];
            toast.error(
                first
                    ? `Product ${first.index + 1} — ${first.missing.map((f) => PRODUCT_FIELD_LABELS[f]).join(", ")} missing`
                    : "Add at least one product line.",
            );
            return;
        }

        setLoading(true);
        setFieldErrors([]);

        // `files` is the dropzone's list: entries picked in this session are raw
        // File objects, entries loaded from an existing order carry a `path`.
        const formData = new FormData();
        const alreadyUploaded = [];
        files.forEach((file) => {
            const raw = file.originFileObj || (file instanceof Blob ? file : null);
            if (raw) formData.append('images[]', raw);
            else if (file.path) alreadyUploaded.push(file);
        });

        let uploaded = [];
        if (!isFormDataEmpty(formData)) {
            try {
                uploaded = await uploadFile(formData);
            } catch (error) {
                const message = (error.response && error.response.data && error.response.data.message)
                    || error.message || error.toString();
                toast.error(`Images failed to upload — ${message}`);
                setLoading(false);
                return;
            }
        }
        if (isEdit && removedFiles.length) {
            removedFiles.forEach((file) => { if (file.path) removeImages(file.path); });
        }
        const images = [...alreadyUploaded, ...uploaded];

        const orderData = isEdit
            ? { ...data, images, createdBy: order.createdBy, orderType: order.orderType, amount: order.payment.amount }
            : { ...data, images, createdBy: user.id, orderType };

        try {
            const response = isEdit
                ? await axiosClient.put(`/orders/update/${order.id}`, orderData)
                : await axiosClient.post(`/orders/store`, orderData);
            toast.success(response.data.message);
            if (!isEdit) localStorage.removeItem(DRAFT_KEY);
            setLoading(false);
            navigate('/orders');
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message)
                || error.message || error.toString();
            toast.error(message);
            setLoading(false);
        }
    };

    // Errors live at the field. The rail carries the counts; nothing scrolls to
    // the top, because where the operator was is where they still are.
    const onFinishFailed = ({ errorFields }) => {
        setSubmitAttempted(true);
        setFieldErrors(errorFields || []);
        const first = (errorFields || [])[0];
        if (first) toast.error(`${errorLabel(first.name)} — ${first.errors?.[0]}`);
    };

    const jumpToSection = (key) => {
        const failing = fieldErrors.find((f) => sectionOfField(f.name) === key);
        if (failing) {
            orderForm.scrollToField(failing.name, { behavior: "smooth", block: "center" });
            return;
        }
        document.getElementById(SECTION_ANCHORS[key])
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const discard = () => {
        Modal.confirm({
            title: isEdit ? "Discard your changes?" : "Discard this draft?",
            content: isEdit
                ? "The order stays as it was. Nothing you changed here is saved."
                : "The saved draft is deleted and the form is cleared.",
            okText: "Discard",
            okButtonProps: { danger: true },
            cancelText: "Keep editing",
            onOk: () => {
                if (!isEdit) localStorage.removeItem(DRAFT_KEY);
                navigate('/orders');
            },
        });
    };

    if (isEdit && orderLoading) {
        return <Loading layout={'default'} />;
    }

    const submitLabel = isEdit ? "Update order" : "Create order";
    const railProps = {
        mode,
        sections: rail.sections,
        done: rail.done,
        total: rail.total,
        percent: rail.percent,
        orderAmount,
        deliveryCharge: Number(values.deliveryCharge) || 0,
        grandTotal,
        submitLabel,
        submitting: loading,
        hasErrors: rail.sections.some((s) => s.errors > 0),
        onJump: jumpToSection,
        draftSavedAt,
        changedCount,
    };

    return (
        <ConfigProvider theme={orderFormTheme(darkMode)}>
            <div className="et-orderform">
                <Form
                    name={isEdit ? "update_order_form" : "order_form"}
                    form={orderForm}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    scrollToFirstError={false}
                    // Nothing validates until the first submit attempt; after that,
                    // blur is the moment an operator has finished with a field.
                    validateTrigger={submitAttempted ? ["onBlur", "onChange"] : []}
                >
                    <header
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 24,
                            flexWrap: "wrap",
                            marginBottom: 20,
                        }}
                    >
                        <div>
                            <h1 style={{ font: "700 22px Outfit, sans-serif", letterSpacing: "-.01em", margin: 0 }}>
                                {isEdit ? `Edit Order #${order.id}` : "Create Order"}
                            </h1>
                            <div style={{ font: "400 13px Outfit, sans-serif", color: "var(--muted)", marginTop: 3 }}>
                                {isEdit ? (
                                    <>Existing order · channel locked to <strong style={{ color: "var(--text)", fontWeight: 600 }}>{channel}</strong></>
                                ) : (
                                    <>New order · draft started <Mono style={{ fontSize: 12.5 }}>{startedAt.format("D MMM, HH:mm")}</Mono></>
                                )}
                            </div>
                        </div>

                        <div className="et-of-headeractions">
                            <Button type="text" onClick={discard} style={{ color: "var(--muted)" }}>Discard</Button>
                            {!isEdit && <Button onClick={() => saveDraft(true)}>Save draft</Button>}
                            <Button type="primary" htmlType="submit" loading={loading}>{submitLabel}</Button>
                        </div>
                    </header>

                    <div className="et-of-grid">
                        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
                            <OrderProductForm
                                productTypes={productTypes}
                                fabricTypes={fabricTypes}
                                productFormations={productFormations}
                                formationsByType={formationsByType}
                                fabrics={fabrics}
                                loadMore={loadMore}
                                hasMore={hasMore}
                                fabricsLoading={fabricsLoading}
                                fetchFabrics={fetchFabrics}
                                onHandFor={onHand}
                                orderForm={orderForm}
                                setFiles={setFiles}
                                files={files}
                                submitAttempted={submitAttempted}
                                isPriceLocked={isPriceLocked}
                                {...(isEdit ? { setRemovedFiles, removedFiles } : {})}
                            />

                            <OrderCustomerForm
                                divisions={divisions}
                                districts={districts}
                                upazilas={upazilas}
                                divisionLoading={divisionLoading}
                                districtLoading={districtLoading}
                                upazilaLoading={upazilaLoading}
                                onDivisionSelect={onDivisionSelect}
                                onDistrictSelect={onDistrictSelect}
                                orderForm={orderForm}
                                channel={channel}
                                marketplaces={allMarketplaces}
                                // No handler in edit mode: an order's channel is fixed
                                // once it exists, and the toggle locks itself on that.
                                {...(isEdit ? {} : {
                                    onChannelChange: (next) => setOrderType(
                                        next === "online" ? OrderTypeEnum.MARKETPLACE : OrderTypeEnum.MERCHANT,
                                    ),
                                })}
                            />

                            <DeliveryFrom isPriceLocked={isPriceLocked} />
                        </div>

                        {/* One rail, three presentations: sticky sidebar at ≥1180px,
                            a strip below the form on tablet, a dock on mobile. The
                            grid collapses to one column below 1180, which is what
                            puts the strip and the dock underneath. */}
                        <OrderFormRail {...railProps} />
                    </div>
                </Form>
            </div>
        </ConfigProvider>
    );
};

export default OrderFormPage;
